import { useQuery } from "@tanstack/react-query";
import fetchData from "@/lib/Fetchers/fetchData";
import { ViewModeConfig } from "@/lib/Helpers/currentUrl";
import { EnvConfig } from "@/lib/Helpers/getServerConfig";
import TourOperator from "@/lib/Types/TourOperator";
import { sendKibanaLog } from "./sendKibanaLog";

interface TourOperatorParams {
  multiroom: boolean;
  profile: number;
  travelStartDate: string;
}

interface TourOperatorHookParams {
  envConfig: EnvConfig | null;
  viewMode: ViewModeConfig | null;
  travelStartDate: string;
  transactionId: string;
  searchId: string;
}

async function fetchTourOperators(
  envConfig: EnvConfig,
  viewMode: ViewModeConfig,
  travelStartDate: string,
  transactionId: string,
  searchId: string,
): Promise<TourOperator[] | null> {
  const paramsObject: TourOperatorParams = {
    multiroom: false,
    profile: viewMode.profile,
    travelStartDate,
  };

  const baseUrl = "comvel-touroperator-service/rest/tour-operators";
  const url = `${envConfig.api}/${baseUrl}`;

  sendKibanaLog({
    envConfig,
    message: "TOUR OPERATOR START",
    component: "tourOperator",
    jsonMessage: paramsObject,
    transactionId,
    searchId: searchId,
    category: "debug",
  });

  const data = await fetchData({
    url,
    method: "GET",
    params: paramsObject,
    envConfig,
    service: "provider",
    transactionId,
    searchId: searchId,
  });

  sendKibanaLog({
    envConfig,
    message: `TOUR OPERATOR ${data?.apiError ? "FAIL" : "SUCCESS"}`,
    component: "tourOperator",
    jsonMessage: data?.apiError && data.message,
    transactionId,
    searchId: searchId,
    category: "debug",
  });

  if (data?.apiError) {
    return null;
  }

  return data;
}

const useTourOperator = ({
  envConfig,
  viewMode,
  travelStartDate,
  transactionId,
  searchId,
}: TourOperatorHookParams) => {
  const query = useQuery({
    queryKey: ["tourOperators", viewMode?.profile, travelStartDate],
    queryFn: () =>
      fetchTourOperators(
        envConfig as EnvConfig,
        viewMode as ViewModeConfig,
        travelStartDate,
        transactionId,
        searchId,
      ),
    staleTime: 5 * 60 * 1000, // 5 minutes - tour operators don't change often
    enabled: !!envConfig && !!viewMode && !!travelStartDate,
  });

  return {
    data: query.data ?? null,
    isLoading: query.isLoading,
    mutate: query.refetch,
  };
};

export default useTourOperator;
